import { Feature, ReleaseMode } from './featureFlags.definition';
import type { FeatureFlagDefinition } from './featureFlags.definition';

/**
 * Email patterns allowed to see features marked `ReleaseMode.INTERNAL`.
 *
 * WARNING: this gates internal-only access in prod - only add trusted internal domains.
 */
export const internalReleaseEmailPatterns: RegExp[] = [
  /^[^@\s]+@getsquareone\.app$/i,
  /^[^@\s]+@emerge\.nz$/i
];

/**
 * Feature flag state (runtime toggles)
 *
 * This file is intentionally separated so that:
 * - Engineers can safely toggle features without touching core definitions
 * - PR reviews for rollout changes remain small and focused
 * - Incident rollback is fast and low-risk
 *
 * WARNING:
 * This file directly impacts production behavior.
 * Every change should be reviewed carefully for user impact.
 */
export const featureFlags = {
  [Feature.PASSKEY_AUTH]: {
    enabled: true,
    description:
      'Controls whether Passkey authentication is available. Used to safely manage rollout, rollback, and production support activities.'
  },
  [Feature.PAYMENT]: {
    enabled: true,
    description:
      'Controls availability of payment functionality. Enables payment creation and processing flows. Used for controlled rollout due to financial and operational risk.'
  },
  [Feature.BATCH_PAYMENT]: {
    enabled: true,
    releaseMode: ReleaseMode.PUBLIC,
    description:
      'Controls availability of batch payment functionality. Enables bulk payment creation and processing flows. Used for controlled rollout due to financial and operational risk.'
  },
  [Feature.SELECTED_ACCOUNT]: {
    enabled: true,
    description:
      'Controls availability of the selected account detail view. Disabled until the backend API is available across all squads in UAT.'
  },
  [Feature.MANAGE_PAYMENT]: {
    enabled: true,
    releaseMode: ReleaseMode.PUBLIC,
    description:
      'Controls availability of the Manage Payment dashboard (scheduled payment and batch payment monitoring - approvals, batch overview, payment history). Covers both scheduled payments and true batch payments; Feature.BATCH_PAYMENT separately controls whether batch groups appear within it. Used for controlled rollout due to financial and operational risk.'
  },
  [Feature.IRD_SCHEDULE]: {
    enabled: false,
    description:
      'Controls whether the "Schedule payment" toggle is shown on the IRD tax payment form. The backend registered-payee payment endpoint has no scheduled variant yet - keep disabled until that lands, to avoid offering a control that cannot work.'
  },
  [Feature.TEAM_MEMBERS]: {
    enabled: false,
    description:
      'Controls availability of the Team Members page (v3 only) - view all members of a business, their access level and card counts.'
  },
  [Feature.CARDS]: {
    enabled: false,
    description:
      'Controls availability of the Cards dashboard - every virtual/physical card issued to the business, who holds it, its status/limits and linked account.'
  }
} as const satisfies Record<Feature, FeatureFlagDefinition>;
